import Calendar from '../Models/Calendar'
import Event from '../Models/Event'
import EditModal from '../Components/EditModal'
import EventMap from '../Components/EventMap';

export default class CalendarView {
    constructor(calendar, identifier) {
        this._startDate; // a moment.js object
        this._endDate; // a moment.js object
        this.calendar = calendar;
        this.identifier = identifier; // Identifies the Calendar Root HTML Element
        this._eventMapKeyFormat;
        this.state = {};
        // collapseCount sets the number of events allowed to fit in one unit box before the collapse/expand button is used
        this.collapseCount = 2;
        const container = document.querySelector(identifier);
        if (!container) {
            throw Error(`Could not find HTML Element: "${identifier}"`);
        }
        this.view = document.createElement('div');
        this.view.className = 'whale-calendar';
        container.append(this.view);
        this.editModal = new EditModal(calendar.eventTypes, calendar.timezone);
        this.editModal.submitCallback = this.handleEditModalSubmit.bind(this);
        this.editModal.closeCallback = this.cancelSelection.bind(this);
    }

    get calendar() {
        return this._calendar;
    }

    set calendar(calendar) {
        if (calendar instanceof Calendar) {
            this._calendar = calendar;
        } else {
            throw Error('Must be a Calendar instance!');
        }
    }

    get identifier() {
        return this._identifier;
    }
    
    set identifier(identifier) {
        if (identifier === undefined) {
            throw Error('The second param must be a valid JS identifier');
        } else if (typeof identifier !== "string") {
            throw Error('identifier must be a string!');
        }
        this._identifier = identifier;
    }

    get startDate() {
        return this._startDate ? this._startDate.clone() : undefined;
    }

    set startDate(startDate) {
        if (!moment.isMoment(startDate) || !startDate.isValid()) {
            throw Error(`${startDate} is not a valid moment instance!`);
        }
        if (this._endDate && this._endDate.unix() < startDate.unix()) {
            throw Error(`Start Date must be earlier than the supplied End Date: ${this.endDate.format('YYYY-MM-DD  HH:mm')}!`);
        }
        this._startDate = startDate.clone();
    }

    get endDate() {
        return this._endDate ? this._endDate.clone() : undefined;
    }

    set endDate(endDate) {
        if (!moment.isMoment(endDate) || !endDate.isValid()) {
            throw Error(`${endDate} is not a valid moment instance!`);
        }
        if (this._startDate && this._startDate.unix() > endDate.unix()) {
            throw Error(`End Date must be later than the supplied Start Date: ${this.startDate.format('YYYY-MM-DD  HH:mm')}!`);
        }
        this._endDate = endDate.clone();
    }

    setStartDateToDefault() {
        let date;
        if (this.endDate) {
            date = this.endDate.clone();
        } else {
            date = moment(); // now
        }
        this.startDate = date.startOf(this.majorUnit);
    }

    setEndDateToDefault() {
        let date;
        if (this.startDate) {
            date = this.startDate.clone();
        } else {
            date = moment(); // now
        }
        this.endDate = date.endOf(this.majorUnit);
    }

    get unit() {
        return this._unit;
    }

    set unit(unit) {
        if (typeof unit !== "string") {
            throw Error('Unit must be a string!');
        } else if (!this.getAllowedUnits().includes(unit)) {
            throw Error(`${unit} is not a valid unit!`);
        }
        this._unit = unit;
    }

    getAllowedUnits() {
        return [
            'hour',
            'day',
            'week',
            'month'
        ];
    }

    get majorUnit() {
        if (!this.getAllowedMajorUnits().includes(this._majorUnit)){
            throw Error(`${this._majorUnit} is not a valid majorUnit, it must be bigger than the unit property!`);
        }
        if (this._majorUnit) {
            return this._majorUnit;
        }
        // Defaults if not set
        switch (this.unit) {
            case 'hour':
                return 'day';
            case 'day':
                return 'week';
            case 'week':
                return 'month';
            case 'month':
                return 'year';
            default: 
                if (!this.unit) {
                    throw Error('Unit must be set before a major unit can be retrieved!');
                } else {
                    throw Error(`Something has gone wrong, ${this.unit} is not a valid unit!`);
                }
        }
    }

    set majorUnit(majorUnit) {
        if (!this._unit) {
            throw Error('You must set the unit property before setting the majorUnit!');
        }
        if (typeof majorUnit !== "string") {
            throw Error('Unit must be a string!');
        } else if (!this.getAllowedMajorUnits().includes(majorUnit)) {
            throw Error(`${majorUnit} is not a valid majorUnit!`);
        }
        this._majorUnit = majorUnit;
    }

    getAllowedMajorUnits() {
        switch (this.unit) {
            case 'hour':
                return ['day', 'week', 'month', 'year'];
            case 'day':
                return ['week', 'month', 'year'];
            case 'week':
                return ['month', 'year'];
            case 'month':
                return ['year'];
            default: 
                if (!this.unit) {
                    throw Error('Unit must be set before a major unit can be retrieved!');
                } else {
                    throw Error(`Something has gone wrong, ${this.unit} is not a valid unit!`);
                }
        }
    }

    getEventMapKeyFormat() {
        switch (this.unit) {
            case 'hour':
                return 'YYYY-MM-DD H';
            case 'day':
                return 'YYYY-MM-DD';
            case 'week':
                // week year - week of the year
                return 'gggg-ww';
            case 'month':
                return 'YYYY-MM';
            default: 
                if (!this.unit) {
                    throw Error('Unit must be set before the view can be built!');
                } else {
                    throw Error(`Something has gone wrong, ${this.unit} is not a valid unit!`);
                }
        }
    }

    // TODO What is the Expected Behaviour???
    getNewDate(date, dateKey) {
        if (!moment.isMoment(date) || !date.isValid()) {
            throw Error(date + ' is not a valid moment instance!');
        }
        const targetDate = this.state.eventMap.getMomentFromKey(dateKey);
        const newDate = date.clone();
        switch (this.unit) {
            case 'hour':
                newDate.year(targetDate.year());
                newDate.month(targetDate.month());
                newDate.date(targetDate.date());
                newDate.hour(targetDate.hour());
                break;
            case 'day':
                newDate.year(targetDate.year());
                newDate.month(targetDate.month());
                newDate.date(targetDate.date());
                break;
            case 'week':
                // week year - week of the year
                newDate.weekYear(targetDate.weekYear());
                newDate.week(targetDate.week());
                break;
            case 'month':
                newDate.year(targetDate.year());
                newDate.month(targetDate.month());
                break;
            default: 
                throw Error(`Something has gone wrong!`);
        }
        return newDate;
    }

    buildEventComponent(event) {
        if (!(event instanceof Event) || !event.isValid()) {
            throw Error('event must be a valid Event Object');
        }
        let dateFormat, eventComponent;

        switch (this.unit) {
            case 'month':
            case 'week':
                dateFormat = 'MM/DD h A';
                break;
            default:
                // day and hour units use the default format
                dateFormat = 'h:mm A';
        }
        const eventRow = document.createElement('div');
        eventRow.className = 'whale-event-row row';
        // Fixed events cannot be moved
        if (!event.fixed) {
            eventRow.classList.add('whale-event-draggable');
            eventRow.setAttribute('draggable', true);
        }
        eventRow.setAttribute('data-unix-start-date', event.startDate.unix());
        eventRow.setAttribute('data-event-id', event.id);

        const eventCol = document.createElement('div');
        eventCol.className = 'whale-event col-xs-12 whale-event-type-' + event._type.toLowerCase();
        eventCol.style = event.style;

        const eventEditOverlay = document.createElement('div');
        eventEditOverlay.className = 'whale-event-edit-overlay';
        const editSpan = document.createElement('span');
        editSpan.className = 'glyphicon glyphicon-pencil edit-date';
        editSpan.setAttribute('aria-hidden', 'true');
        eventEditOverlay.append(editSpan);
        eventCol.append(eventEditOverlay);

        if (event.viewComponents[this.viewName] === undefined) {
            const dateText = event.startDate.format(dateFormat);
            eventComponent = event.getDefaultViewComponent(dateText);
            eventComponent.querySelector('.start-date').setAttribute('data-date-format', dateFormat);
        } else {
            eventComponent = event.viewComponents[this.viewName];
        }
        eventCol.append(eventComponent);

        eventRow.append(eventCol);

        return eventRow;
    }

    getControls() {
        // Displays the Range of dates being shown
        const controls = document.createElement('div');
        controls.className = 'whale-controls';
        // Displays the current view's start and end (Ex: 2019, January, Sunday)
        const header = this.getHeader();
        controls.append(header);

        const buttonContainer = document.createElement('div');
        buttonContainer.className = 'whale-controls-btn-container';

        buttonContainer.append(this.getPreviousButton(header));
        buttonContainer.append(this.getNextButton(header));

        controls.append(buttonContainer);

        return controls;
    }

    getHeader() {
        const header = document.createElement('h2');
        header.className = 'whale-header';
        header.textContent = this.getHeaderText();
        return header;
    }

    getPreviousButton(header) {
        const prevButton = document.createElement('button');
        prevButton.className = 'whale-controls-btn';
        prevButton.textContent = 'prev';
        prevButton.addEventListener('click', (e) => {
            const diff = this.endDate.diff(this.startDate, this.majorUnit) + 1;
            this.startDate = this.startDate.subtract(diff, this.majorUnit);
            this.endDate = this.endDate.subtract(diff, this.majorUnit);
            header.textContent = this.getHeaderText();
            this.render();
        })
        return prevButton;
    }

    getNextButton(header) {
        const nextButton = document.createElement('button');
        nextButton.className = 'whale-controls-btn';
        nextButton.textContent = 'next';
        nextButton.addEventListener('click', (e) => {
            const diff = this.endDate.diff(this.startDate, this.majorUnit) + 1;
            this.endDate = this.endDate.add(diff, this.majorUnit);
            this.startDate = this.startDate.add(diff, this.majorUnit);
            header.textContent = this.getHeaderText();
            this.render();
        })
        return nextButton;
    }
    
    getHeaderText() {
        // Text is dependent on the units being displayed
        let text;
        const oneUnitDiff = this.endDate.diff(this.startDate, this.majorUnit, true) <= 1;
        switch (this.majorUnit) {
            case 'day':
                if (oneUnitDiff) {
                    text = this.startDate.format('MM/DD/YY');
                } else {
                    text = `${this.startDate.format('MM/DD/YY')} - ${this.endDate.format('MM/DD/YY')}`;
                }
                break;
            case 'week':
                text = `${this.startDate.format('MM/DD/YY')} - ${this.endDate.format('MM/DD/YY')}`;
                break;
            case 'month':
                if (oneUnitDiff) {
                    text = this.startDate.format('MMM \'YY');
                } else {
                    text = `${this.startDate.format('MMM \'YY')} - ${this.endDate.format('MMM \'YY')}`;
                }
                break;
            case 'year':
                if (oneUnitDiff) {
                    text = this.startDate.format('YYYY');
                } else {
                    text = `${this.startDate.format('YYYY')} - ${this.endDate.format('YYYY')}`;
                }
                break;
            default: 
                throw Error('You must select your units for the View');
        }
        return text;
    }

    updateUiForDateKey(dateKey) {
        const target = this.view.querySelector(`.whale-events-droppable[data-date-key="${dateKey}"]`);
        if (!target) {
            throw Error("You must provide a valid dateKey with a valid UI target currently in the DOM!");
        }
        target.innerHTML = '';
        let count = 0;
        if (this.state.eventMap[dateKey] !== undefined) {
            this.state.eventMap[dateKey].forEach((event) => {
                if (typeof this.calendar.eventTypes === 'object' && !this.calendar.eventTypes[event.type]) {
                    // skip this event
                    return;
                }
                count++;
                if (event.viewComponents[this.viewName] === undefined) {
                    event.viewComponents[this.viewName] = this.buildEventComponent(event);
                }
                target.appendChild(event.viewComponents[this.viewName]);
            })
        }
        const placeholderEventRow = document.createElement('div');
        placeholderEventRow.className = 'whale-event-row-placeholder';
        target.appendChild(placeholderEventRow);

        if (count > this.collapseCount) {
            target.classList.add('collapsed');
            const collapseButton =  document.createElement('button');
            collapseButton.className = 'expand-collapse-btn';
            collapseButton.textContent = 'Show More';
            collapseButton.addEventListener('click', function(e) {
                const targetHTMLNode = this.closest('.whale-events-droppable');
                if (targetHTMLNode.className.includes('collapsed')) {
                    targetHTMLNode.classList.remove('collapsed');
                    this.textContent = 'Hide';
                } else {
                    targetHTMLNode.classList.add('collapsed');
                    this.textContent = 'Show More';
                }
            })
            target.append(collapseButton);
        }
    }

    // A meta function that updates all the Views associated with this calendar
    updateEventComponents(event) {
        if (!event) {
            throw Error('You must provide event object!');
        }
        // Update on all Calendar's Views
        this.calendar.views.forEach(function(viewObject) {
            // get dateKey
            // TODO This should probably be in the event class or and event component class
            const dateKey = viewObject.state.eventMap.getKeyFromMoment(event.startDate);
            let eventUi;
            if (event.viewComponents[viewObject.viewName] === undefined) {
                eventUi = viewObject.buildEventComponent(event);
                event.viewComponents[viewObject.viewName] = eventUi;
            } else {
                eventUi = event.viewComponents[viewObject.viewName];
               
                const uiStartDateSpan = eventUi.querySelector('.start-date');
                uiStartDateSpan.textContent = event.startDate.format(uiStartDateSpan.getAttribute('data-date-format'));
                eventUi.setAttribute('data-date-key', dateKey);
               
                const typeSpan = eventUi.querySelector('.whale-type');
                typeSpan.className = `whale-type ${event.type}`;
                typeSpan.textContent = `${event.type}: `;

                const eventDiv = eventUi.querySelector('.whale-event');
                eventDiv.className = 'whale-event col-xs-12 whale-event-type-' + event.type.toLowerCase();
            }
            const target = viewObject.view.querySelector(`.whale-events-droppable[data-date-key="${dateKey}"]`);
            if (!target) {
                eventUi.remove();
            } else {
                viewObject.updateUiForDateKey(dateKey);
            }
        });
    }

    // since users enter this by hand it saves a more precise time than drag/select and drop
    handleEditModalSubmit(e, event, updateValues) {
        if (!updateValues || updateValues === {}) {
            this.cancelSelection();
            return;
        }
        const { updateEvent } = this.calendar.eventCallbacks;
        const { selectedElement } = this.state;
        let oldValues = {};
        Object.keys(updateValues).forEach((key) => {
            oldValues[key] = event[key];
            if (key === 'startDate') {
                event.startDate = updateValues[key];
                EventMap.updateAllCalendarMaps(this.calendar, event);
            } else {
                event[key] = updateValues[key];
            }
        });

        if (updateEvent !== undefined) {
            try {
                const revertCallback = () => {
                    this.state.selectedElement = selectedElement;
                    Object.keys(oldValues).forEach((key) => {
                        if (key === 'startDate') {
                            event.startDate = oldValues[key];
                            EventMap.updateAllCalendarMaps(this.calendar, event);
                        } else {
                            event[key] = oldValues[key];
                        }
                    });
                    this.updateEventComponents(event);
                }
                updateEvent(e, event, updateValues, revertCallback, () => this.updateEventComponents(event));
            } catch(err) {
                // if the updateEvent callback fails we shouldnt update the ui
                console.error(err);
                this.cancelSelection();
                return;
            }
        }
        this.updateEventComponents(event);
        this.cancelSelection();
    }

    handleOpenEditModal(e) {
        e.preventDefault(); // prevents ghost click from trigger along with touch
        e.stopPropagation(); // prevents handleEventSelection() from triggering
        const { openEditModal } = this.calendar.eventCallbacks;

        this.editModal.event = this.calendar.events[this.state.selectedId];

        if (openEditModal !== undefined) {
            openEditModal(e, this.editModal);
        }
        this.editModal.toggle();
    }

    addEventListeners() {
        const supportsTouch = 'ontouchend' in window || navigator.msMaxTouchPoints;

        this.view.querySelectorAll('.whale-event-draggable').forEach((event) => {      
            if (event.getAttribute('events-added')) {
                return;
            }
            if (supportsTouch) {
                event.addEventListener('touchstart', this.handleEventSelectionTouchstart.bind(this), false);
                event.addEventListener('touchend', this.handleEventSelection.bind(this), false);
            } else {
                event.addEventListener("dragstart", this.handleDragstart.bind(this), false);
                event.addEventListener("dragend", this.handleDragend.bind(this), false);
                event.addEventListener('click', this.handleEventSelection.bind(this), false);
            }
            event.setAttribute('events-added', true);
        })

        this.view.querySelectorAll('.whale-events-droppable').forEach((eventsCol) => {            
            if (eventsCol.getAttribute('events-added')) {
                return;
            }
            if (supportsTouch) {
                eventsCol.addEventListener("touchstart", this.handleDropSelectedTouchstart.bind(this), false);
                eventsCol.addEventListener("touchend", this.handleDropSelected.bind(this), false);
            } else {
                eventsCol.addEventListener("dragover", this.handleDragover.bind(this), false);
                eventsCol.addEventListener("dragenter", this.handleDragenter.bind(this), false);
                eventsCol.addEventListener("dragleave", this.handleDragleave.bind(this), false);
                eventsCol.addEventListener("drop", this.handleDrop.bind(this), false);
    
                eventsCol.addEventListener("click", this.handleDropSelected.bind(this), false);
                eventsCol.addEventListener("mouseenter", this.handleMouseenterWhileSelected.bind(this), false);
                eventsCol.addEventListener("mouseleave", this.handleMouseleaveWhileSelected.bind(this), false);
            }
            eventsCol.setAttribute('events-added', true);
        })
        this.view.querySelectorAll('span.edit-date').forEach((icon) => {
            if (icon.getAttribute('events-added')) {
                return;
            }
            if (supportsTouch) {
                icon.addEventListener('touchend', this.handleOpenEditModal.bind(this), false);
            } else {
                icon.addEventListener('click', this.handleOpenEditModal.bind(this), false);
            }
            icon.setAttribute('events-added', true);
        });
    }

    handleDragstart(e) {
        const { dragstart } = this.calendar.eventCallbacks;
        this.state.selectedElement = e.target;
        if (dragstart !== undefined) {
            dragstart(e, this.dragged);
        } else {
            e.target.style.opacity = .5;
        }
    }

    handleDragend(e) {
        const { dragend } = this.calendar.eventCallbacks;

        if (dragend !== undefined) {
            dragend(e, this.state.selectedElement);
        } else {
            e.target.style.opacity = 1;
            this.view.querySelector('.whale-calendar-hover').classList.remove('whale-calendar-hover');
        }
        this.state.selectedElement = undefined;
    }

    handleDrop(e) {
        const { drop, updateEvent } = this.calendar.eventCallbacks;
        let event = this.calendar.events[this.state.selectedElement.getAttribute('data-event-id')];
        const dateKey = e.currentTarget.getAttribute('data-date-key');
        if (dateKey === this.state.eventMap.getKeyFromMoment(event.startDate)) {
            this.cancelSelection();
            return;
        }
        const newDate = this.getNewDate(event.startDate, dateKey);
        try {
            if (drop !== undefined) {
                drop(e, this.state.selectedElement, event);
            }
            if (updateEvent !== undefined) {
                const selectedElement = this.state.selectedElement;
                const oldDate = event.startDate;
                const revertCallback = () => {
                    this.state.selectedElement = selectedElement;
                    event.startDate = oldDate;
                    EventMap.updateAllCalendarMaps(this.calendar, event);
                    this.updateEventComponents(event);
                }
                updateEvent(e, event, {startDate: newDate}, revertCallback, () => this.updateEventComponents(event));
            }
        } catch(e) {
            // if the drop callback fails we shouldnt update the ui
            console.error(e);
            this.cancelSelection();
            return;
        }
        event.startDate = newDate;
        EventMap.updateAllCalendarMaps(this.calendar, event);
        this.updateEventComponents(event);
    }

    handleDragenter(e) {
        const { dragenter } = this.calendar.eventCallbacks;

        e.preventDefault();
        if (dragenter !== undefined) {
            dragenter(e, this.state.selectedElement);
        } else {
            if (e.currentTarget.className.includes('invalid-slot')) {
                return;
            }
            if (!e.currentTarget.className.includes('whale-calendar-hover')) {
                const currentHover = this.view.querySelector('.whale-calendar-hover');
                if (currentHover) {
                    currentHover.classList.remove('whale-calendar-hover');
                }
                e.currentTarget.classList.add('whale-calendar-hover');
            }
        }
    }

    handleDragleave(e) {
        const { dragleave } = this.calendar.eventCallbacks;

        if (dragleave !== undefined) {
            dragleave(e, this.state.selectedElement);
        }
    }

    handleDragover(e) {
        const { dragover } = this.calendar.eventCallbacks;

        e.preventDefault();
        if (dragover !== undefined) {
            dragover(e, this.state.selectedElement);
        }
    }

    handleEventSelectionTouchstart(e) {
        // store some touch coordinates to measure touch movement
        this.state.touchstartCoordinates = {
            x: e.changedTouches[0].clientX, 
            y: e.changedTouches[0].clientY
        }
    }

    handleEventSelection(e) {
        if (e.type === 'touchend') {
            // Check if the finger was dragged
            const xMovement = Math.abs(e.changedTouches[0].clientX - this.state.touchstartCoordinates.x);
            const yMovement = Math.abs(e.changedTouches[0].clientY - this.state.touchstartCoordinates.y);
            if (xMovement > 50 || yMovement > 50) {
                return;
            }
        }
        let eventUI;
        if (e.target.className.includes('whale-event-draggable')) {
            eventUI = e.target;
        } else {
            eventUI = e.target.closest('.whale-event-draggable');
        }
        const eventId = eventUI.getAttribute('data-event-id');

        if (this.state.selectedId && eventId !== this.state.selectedId) {
            return;
        }
        // prevent events from triggering on parent elements 
        e.stopPropagation();

        // Remove selection if clicked again
        if (eventUI.className.includes('event-selected')) {
            this.cancelSelection();
        } else {
            this.state.selectedClone = eventUI.cloneNode(true);
            this.state.selectedClone.classList.add('event-temp');
            this.state.selectedClone.setAttribute('data-event-id', '');

            eventUI.classList.add('event-selected');
            if (e.type === 'touchend') {
                this.view.classList.add('active-selection');
            }
            this.state.selectedId = eventId;
            this.state.selectedElement = eventUI;

        }
    }

    cancelSelection() {
        this.view.classList.remove('active-selection');
        if (this.state.selectedElement) {
            this.state.selectedElement.classList.remove('event-selected');
            this.state.selectedElement = null;
        }
        this.state.selectedId = null;
        if (this.state.selectedClone) {
            this.state.selectedClone.remove();
        }
    }

    handleMouseenterWhileSelected(e) {
        if (!this.state.selectedId) {
            return;
        }
        if (e.target.querySelector(`[data-event-id="${this.state.selectedId}"]`)) {
            return;
        }
        if (e.target.className.includes('invalid-slot')) {
            return;
        }
        const collapseButton = e.target.querySelector('.expand-collapse-btn');
        e.target.insertBefore(this.state.selectedClone, collapseButton); // inserts at the end if collapseButton is Null
    }

    handleMouseleaveWhileSelected(e) {
        if (!this.state.selectedId) {
            return;
        }
        this.state.selectedClone.remove();
    }

    handleDropSelectedTouchstart(e) {
        if (!this.state.selectedId) {
            return;
        }
        // store some touch coordinates to measure touch movement
        this.state.touchstartCoordinates = {
            x: e.changedTouches[0].clientX, 
            y: e.changedTouches[0].clientY
        }
    }

    handleDropSelected(e) {
        if (!this.state.selectedId) {
            return;
        }
        if (e.type === 'touchend') {
            // Check if the finger was dragged
            const xMovement = Math.abs(e.changedTouches[0].clientX - this.state.touchstartCoordinates.x);
            const yMovement = Math.abs(e.changedTouches[0].clientY - this.state.touchstartCoordinates.y);
            if (xMovement > 50 || yMovement > 50) {
                return;
            }
        }

        let eventsCol;
        if (e.target.className.includes('whale-events-droppable')) {
            eventsCol = e.target;
        } else {
            eventsCol = e.target.closest('.whale-events-droppable');
        }
        if (eventsCol.querySelector(`[data-event-id="${this.state.selectedId}"]`)) {
            return;
        }
        let event = this.calendar.events[this.state.selectedId];
        const { updateEvent } = this.calendar.eventCallbacks;
        const dateKey = eventsCol.getAttribute('data-date-key');
        const newDate = this.getNewDate(event.startDate, dateKey);
        if (updateEvent !== undefined) {
            try {
                const selectedElement = this.state.selectedElement;
                const oldDate = event.startDate;
                const revertCallback = () => {
                    this.state.selectedElement = selectedElement;
                    event.startDate = oldDate;
                    EventMap.updateAllCalendarMaps(this.calendar, event);
                    this.updateEventComponents(event);
                }
                updateEvent(e, event, {startDate: newDate}, revertCallback, () => this.updateEventComponents(event));
            } catch(e) {
                // if the updateEvent callback fails we shouldnt update the ui
                console.error(e);
                this.cancelSelection();
                return;
            }
        }
        event.startDate = newDate;
        EventMap.updateAllCalendarMaps(this.calendar, event);
        this.updateEventComponents(event);
        this.cancelSelection();
    }
}
